import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './NodeDetailPage.css';

interface NodeItem {
  id: string;
  name: string;
  ip: string;
  threats: number;
  protected: boolean;
  policies: number;
  status: string;
  cpuUsage: number;
  memoryUsage: number;
  lastSeen: string;
}

const NodeDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedItem, setSelectedItem] = useState<NodeItem | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [protectionFilter, setProtectionFilter] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const nodeData: NodeItem[] = useMemo(() => ([
    {
      id: 'node-1',
      name: 'worker-01',
      ip: '10.0.1.10',
      threats: 0,
      protected: true,
      policies: 12,
      status: 'Healthy',
      cpuUsage: 25,
      memoryUsage: 45,
      lastSeen: '2024-01-16 14:32:15'
    },
    {
      id: 'node-2',
      name: 'worker-02',
      ip: '10.0.1.11',
      threats: 2,
      protected: true,
      policies: 12,
      status: 'Warning',
      cpuUsage: 65,
      memoryUsage: 72,
      lastSeen: '2024-01-16 14:32:10'
    },
    {
      id: 'node-3',
      name: 'master-01',
      ip: '10.0.1.5',
      threats: 0,
      protected: true,
      policies: 15,
      status: 'Healthy',
      cpuUsage: 45,
      memoryUsage: 58,
      lastSeen: '2024-01-16 14:32:12'
    },
    {
      id: 'node-4',
      name: 'worker-03',
      ip: '10.0.1.12',
      threats: 5,
      protected: true,
      policies: 12,
      status: 'Critical',
      cpuUsage: 88,
      memoryUsage: 85,
      lastSeen: '2024-01-16 14:30:45'
    },
    {
      id: 'node-5',
      name: 'data-store-01',
      ip: '10.0.1.20',
      threats: 1,
      protected: false,
      policies: 5,
      status: 'Warning',
      cpuUsage: 35,
      memoryUsage: 62,
      lastSeen: '2024-01-16 14:25:30'
    },
  ]), []);

  const filteredNodes = useMemo(() => {
    return nodeData.filter(item => {
      const statusMatch = statusFilter === 'all' || item.status === statusFilter;
      const protectionMatch = protectionFilter === 'all' || 
        (protectionFilter === 'protected' && item.protected) || 
        (protectionFilter === 'unprotected' && !item.protected);
      return statusMatch && protectionMatch;
    });
  }, [statusFilter, protectionFilter, nodeData]);

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Healthy': return '#22c55e';
      case 'Warning': return '#ffd700';
      case 'Critical': return '#ff4444';
      default: return '#666666';
    }
  };

  if (!selectedItem && filteredNodes.length > 0) {
    setSelectedItem(filteredNodes[0]);
  }


  return (
    <div className="node-detail-container">
      <div className="detail-header">
        <h1>{t('nodeHost.detailTitle')}</h1>
        <p>{t('nodeHost.detailDescription')}</p>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>{t('nodeHost.filters.status')}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">{t('nodeHost.filters.all')}</option>
            <option value="Healthy">Healthy</option>
            <option value="Warning">Warning</option>
            <option value="Critical">Critical</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('nodeHost.filters.namespace')}</label>
          <select value={protectionFilter} onChange={(e) => setProtectionFilter(e.target.value)}>
            <option value="all">{t('nodeHost.filters.all')}</option>
            <option value="protected">Protected</option>
            <option value="unprotected">Unprotected</option>
          </select>
        </div>

        <div className="filter-stat">
          {t('nodeHost.filters.searchResults')}: {filteredNodes.length}
        </div>
      </div>

      <div className="content-layout">
        <div className="table-section">
          <div className="table-header">
            <div>{t('nodeHost.columns.nodeName')}</div>
            <div>{t('nodeHost.columns.ip')}</div>
            <div>{t('nodeHost.columns.threats')}</div>
            <div>{t('nodeHost.columns.status')}</div>
            <div>{t('nodeHost.columns.protection')}</div>
            <div>{t('nodeHost.columns.policies')}</div>
          </div>

          <div className="table-body">
            {filteredNodes.length > 0 ? (
              filteredNodes.map(item => (
                <div
                  key={item.id}
                  className={`table-row ${selectedItem?.id === item.id ? 'active' : ''}`}
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="table-row-cell">{item.name}</div>
                  <div className="table-row-cell">{item.ip}</div>
                  <div className="table-row-cell">{item.threats}</div>
                  <div className="table-row-cell">
                    <span className="status-badge" style={{backgroundColor: getStatusColor(item.status) + '20', color: getStatusColor(item.status)}}>
                      {item.status}
                    </span>
                  </div>
                  <div className="table-row-cell">{item.cpuUsage}%</div>
                  <div className="table-row-cell">{item.memoryUsage}%</div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>No nodes found</p>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          {selectedItem ? (
            <div className="detail-card">
              <h2>{selectedItem.name}</h2>
              <div className="detail-row">
                <label>ID</label>
                <div className="detail-row-value">{selectedItem.id}</div>
              </div>
              <div className="detail-row">
                <label>IP Address</label>
                <div className="detail-row-value">{selectedItem.ip}</div>
              </div>
              <div className="detail-row">
                <label>Status</label>
                <div className="detail-row-value">
                  <span className="detail-badge" style={{backgroundColor: getStatusColor(selectedItem.status) + '20', color: getStatusColor(selectedItem.status)}}>
                    {selectedItem.status}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <label>Threats Detected</label>
                <div className="detail-row-value">{selectedItem.threats}</div>
              </div>
              <div className="detail-row">
                <label>Protected</label>
                <div className="detail-row-value">
                  {selectedItem.protected ? '✓ Yes' : '✗ No'}
                </div>
              </div>
              <div className="detail-row">
                <label>Policies Applied</label>
                <div className="detail-row-value">{selectedItem.policies}</div>
              </div>
              <div className="detail-row">
                <label>CPU Usage</label>
                <div className="detail-row-value">{selectedItem.cpuUsage}%</div>
              </div>
              <div className="detail-row">
                <label>Memory Usage</label>
                <div className="detail-row-value">{selectedItem.memoryUsage}%</div>
              </div>
              <div className="detail-row">
                <label>Last Seen</label>
                <div className="detail-row-value">{selectedItem.lastSeen}</div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-detail">
              <div className="empty-state-message">
                <p>📋 목록에서 노드를 선택하세요.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NodeDetailPage;
